import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { QuestionBody } from "./QuestionBody";

export type ProseSection = {
  heading: string;
  paragraphs: string[];
  /** Anchor id for in-page links (rendered on the section wrapper). */
  id?: string;
  /** Numbered points rendered under the paragraphs ("01 " prefixes). */
  points?: string[];
  /** Plain bullet list rendered under the paragraphs. */
  bullets?: string[];
  /** Small caption-style source line under the section. */
  note?: string;
};

const linkStyle = { color: "#002FA7", textDecoration: "underline", textUnderlineOffset: 3 } as const;

/** Renders [label](/path) markers as router links. External URLs open in a new tab. Unknown paths render as plain text. */
export function renderRichText(text: string): ReactNode[] {
  const parts: ReactNode[] = [];
  const re = /\[([^\]]+)\]\(([^)]+)\)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const label = m[1] ?? "";
    const path = m[2] ?? "";
    const k = parts.length;
    if (path.startsWith("http://") || path.startsWith("https://")) {
      parts.push(<a key={k} href={path} target="_blank" rel="noopener" style={linkStyle}>{label}</a>);
    } else if (path.startsWith("/blog/")) {
      parts.push(<Link key={k} to="/blog/$slug" params={{ slug: path.slice(6) }} style={linkStyle}>{label}</Link>);
    } else if (path === "/services/local-services-ads-for-roofers") {
      parts.push(<Link key={k} to="/services/local-services-ads-for-roofers" style={linkStyle}>{label}</Link>);
    } else if (["/phoenix-ppc-agency", "/scottsdale-ppc-agency", "/mesa-ppc-agency", "/chandler-ppc-agency", "/arizona-home-services-ppc-agency", "/google-ads-for-hvac-companies-arizona", "/google-ads-for-roofing-companies-arizona", "/case-studies"].includes(path)) {
      parts.push(<Link key={k} to={path as "/phoenix-ppc-agency"} style={linkStyle}>{label}</Link>);
    } else if (path.startsWith("/services/")) {
      parts.push(<Link key={k} to="/services/$service" params={{ service: path.slice(10) }} style={linkStyle}>{label}</Link>);
    } else if (path.startsWith("/industries/")) {
      parts.push(<Link key={k} to="/industries/$industry" params={{ industry: path.slice(12) }} style={linkStyle}>{label}</Link>);
    } else if (path === "/research/conversion-tracking-report") {
      parts.push(<Link key={k} to="/research/conversion-tracking-report" style={linkStyle}>{label}</Link>);
    } else if (path === "/audit") {
      parts.push(<Link key={k} to="/audit" style={linkStyle}>{label}</Link>);
    } else if (path === "/contact") {
      parts.push(<Link key={k} to="/contact" style={linkStyle}>{label}</Link>);
    } else {
      parts.push(label);
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

export function RichProse({ sections }: { sections: ProseSection[] }) {
  return (
    <section className="bhg-section">
      <div className="wrap">
        {sections.map((block) => (
          <div
            key={block.heading}
            id={block.id}
            className="section-split prose-split mb-12 last:mb-0"
            style={block.id ? { scrollMarginTop: 96 } : undefined}
          >
            <h2 className="section-heading"
              style={{
                fontFamily: '"IBM Plex Sans", "IBM Plex Sans Fallback", sans-serif',
                fontWeight: 300,
                fontSize: "clamp(22px, 2vw, 26px)",
                letterSpacing: "-0.01em",
                color: "#002FA7",
                margin: 0,
              }}
            >
              {block.heading}
            </h2>
            <div className="section-body"><QuestionBody>{block.paragraphs.map((p, i) => (
              <p
                key={i}
                className="mt-5"
                style={{
                  fontSize: 16.5,
                  lineHeight: 1.6,
                  color: "rgba(0,47,167,0.78)",
                  maxWidth: "62ch",
                  margin: i === 0 ? undefined : "20px 0 0",
                }}
              >
                {renderRichText(p)}
              </p>
            ))}
            {block.points ? (
              <ul className="rich-points">
                {block.points.map((point) => {
                  const m = /^(\d\d)\s+([\s\S]*)$/.exec(point);
                  return (
                    <li key={point} className="rich-point">
                      <span className="mono rich-point-num">{m?.[1]}</span>
                      <span>{renderRichText(m?.[2] ?? point)}</span>
                    </li>
                  );
                })}
              </ul>
            ) : null}
            {block.bullets ? (
              <ul className="rich-points">
                {block.bullets.map((b) => (
                  <li key={b} className="rich-point">
                    <span className="mono rich-point-num" aria-hidden="true">·</span>
                    <span>{renderRichText(b)}</span>
                  </li>
                ))}
              </ul>
            ) : null}
            </QuestionBody>
            {block.note ? <p className="mono rich-note">{renderRichText(block.note)}</p> : null}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
